/**
 * Artifact Position show — placement detail on the shared Inspector grammar
 * (design-system §5). An ArtifactPosition is where an artifact sits within a
 * contour: a single phase (strong, `is_primary`) or a scored mode (weak).
 *
 * Primary action **Advance** (frontend-entity-coverage §3): produces a
 * `transition_phase` effect (strong) or a `transition_proposal` (weak/agent).
 * The backing is `[PROPOSED]`; the action routes through the sanctioned write
 * path and degrades gracefully (informative toast, never a crash) until it
 * ships. Edit/Delete live in the overflow menu. Relations link the Artifact,
 * the contour Package, the Phase, and the last Event.
 */
import {
  useShow,
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { ArrowUpRight, FileBox, Layers, Pencil, Trash2, Workflow, Zap } from "@/components/ui/icon";
import type { ArtifactPosition } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  type RelationGroup,
  TechnicalId,
} from "@/components/inspector";
import { ShowPageSkeleton, ShowPageError } from "@/components/inspector/ShowPage";
import { formatDateTime } from "../_shared/runs-ui";
import { useApiAction } from "../_shared/useApiAction";

export function ArtifactPositionShow() {
  const go = useGo();
  const { editUrl, listUrl } = useNavigation();
  const { mutate: deletePosition } = useDelete();
  const { run, loading } = useApiAction();

  const { query } = useShow<ArtifactPosition, HttpError>({
    resource: "artifact-positions",
  });
  const { data, isLoading, isError, error, refetch } = query;
  const pos = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !pos) {
    return (
      <ShowPageError
        message={error?.message ?? "Position not found."}
        onRetry={() => void refetch()}
      />
    );
  }

  const advance = () => {
    void run(`/artifact-positions/${pos.id}/advance`, {
      invalidateResources: [
        "artifact-positions",
        "transition-proposals",
        "events",
      ],
      successMessage: "Advance requested.",
    });
  };

  const relations: RelationGroup[] = [
    {
      title: "References",
      items: [
        { label: "Artifact", icon: FileBox, resource: "artifacts", id: pos.artifact_id },
        { label: "Contour package", icon: Layers, resource: "packages", id: pos.process_id },
        { label: "Phase", icon: Workflow, resource: "phases", id: pos.phase_id },
        { label: "Last event", icon: Zap, resource: "events", id: pos.last_event_id },
      ],
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title="Artifact Position"
        subtitle="Where an artifact sits within a contour — its phase, mode, and transitions."
        status={pos.is_primary === 1 ? "active" : "partial"}
        primaryAction={
          <Button
            size="sm"
            onClick={advance}
            disabled={loading}
            data-testid="artifact-position-advance"
          >
            <ArrowUpRight className="mr-2 h-4 w-4" />
            {loading ? "Working…" : "Advance"}
          </Button>
        }
        menuActions={[
          {
            label: "Edit",
            icon: Pencil,
            onClick: () => go({ to: editUrl("artifact-positions", pos.id) }),
          },
          {
            label: "Delete",
            icon: Trash2,
            destructive: true,
            onClick: () =>
              deletePosition(
                { resource: "artifact-positions", id: pos.id },
                { onSuccess: () => go({ to: listUrl("artifact-positions") }) },
              ),
          },
        ]}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Artifact">
            <RefLink resource="artifacts" id={pos.artifact_id} />
          </Field>
          <Field label="Contour package">
            <RefLink resource="packages" id={pos.process_id} />
          </Field>
          <Field label="Phase / mode">{pos.phase_id}</Field>
          <Field label="Primary">
            {pos.is_primary === 1 ? (
              <Badge variant="default">Primary</Badge>
            ) : (
              <Badge variant="secondary">Partial</Badge>
            )}
          </Field>
          <Field label="Membership score">
            {pos.membership_score.toFixed(2)}
          </Field>
          <Field label="Confidence">
            {pos.confidence_score != null
              ? pos.confidence_score.toFixed(2)
              : undefined}
          </Field>
          <Field label="Entered at">{formatDateTime(pos.entered_at)}</Field>
          <Field label="Updated">{formatDateTime(pos.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock groups={relations} />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(pos.id)} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock
          entityType="artifact-positions"
          entityId={pos.id}
          provenance={[
            { label: "Last event", resource: "events", id: pos.last_event_id },
            { label: "Artifact", resource: "artifacts", id: pos.artifact_id },
          ]}
        />
      </InspectorSection>
    </Inspector>
  );
}

export default ArtifactPositionShow;
